import React, { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import type { TFunction } from 'next-i18next';
import { LOCALE_HOME_PATHS, Namespaces } from 'types/locales';
import { buildClassName } from 'utils/build-class-name';
import { formatRatio } from 'utils/format-ratio';
import Link from 'components/link';
import type { InsightsListingType } from 'data/insights/types';
import ScrollReveal, { StaggerReveal, AnimatedCounter, ProgressFill } from '../scroll-reveal';
import styles from './style.module.scss';
import parentStyles from '../style.module.scss';
import skeleton from '../skeleton.module.scss';

// ── Types ───────────────────────────────────────────────────

export interface PropertyTypeSegment {
  label: string;
  count: number;
  color: string;
}

export interface MarketVolumeData {
  totalListings: number;
  mostActiveRange: string;  // e.g. "$1M - $1.5M"
  priceRanges: {
    label: string;
    percentage: number;
    isActive: boolean;
  }[];
}

export interface PriceTrendPoint {
  date: string;
  price: number;
}

export interface MarketHealthData {
  daysOnMarket: number;
  sellListRatio: number;
  avgPricePerSqft: number;
  medianRentPrice: number;
}

interface MarketDynamicsProps {
  locationName: string;
  propertyTypes: PropertyTypeSegment[];
  volume: MarketVolumeData | null;
  /** True while new insights data is loading */
  isLoading?: boolean;
  /** Monthly median price trend data */
  priceTrends?: PriceTrendPoint[];
  /** Key market health metrics */
  marketHealth?: MarketHealthData;
  /**
   * Pre-computed URL for the current area page. Omitted when the location should read as
   * plain text — AreaPageV2's Insights tab is already that area's page, so a link there
   * went nowhere new and only added a hover underline mid-heading.
   */
  locationUrl?: string;
  /** Optional supporting copy rendered directly below the section heading. */
  description?: string;
  /** Market the data describes; switches the card labels. Defaults to homes for sale. */
  listingType?: InsightsListingType;
}

// ── Default / placeholder data ──────────────────────────────

export const DEFAULT_PROPERTY_TYPES: PropertyTypeSegment[] = [
  { label: 'House', count: 420, color: '#6ee7b7' },
  { label: 'Condo Apt', count: 580, color: '#67b8f0' },
  { label: 'Townhouse', count: 260, color: '#1b489b' },
  { label: 'Semi-Detached', count: 120, color: '#f97066' },
  { label: 'Other', count: 102, color: '#c4b5fd' },
];

export const DEFAULT_VOLUME: MarketVolumeData = {
  totalListings: 1482,
  mostActiveRange: '$1M - $1.5M',
  priceRanges: [
    { label: '$500k - $1M', percentage: 18, isActive: false },
    { label: '$1M - $1.5M', percentage: 42, isActive: true },
    { label: '$1.5M - $2.5M', percentage: 25, isActive: false },
    { label: '$2.5M - $5M', percentage: 10, isActive: false },
    { label: '$5M+', percentage: 5, isActive: false },
  ],
};

// ── Helpers ─────────────────────────────────────────────────

function formatMonth(dateStr: string): string {
  const [year, month] = dateStr.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month) - 1]} '${year.slice(-2)}`;
}

/** Map an English property-type label (produced by derivation) to its translation. */
const PROPERTY_TYPE_LABEL_KEYS: Record<string, string> = {
  House: LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_PROPERTY_TYPE_HOUSE,
  'Condo Apt': LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_PROPERTY_TYPE_CONDO_APT,
  Townhouse: LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_PROPERTY_TYPE_TOWNHOUSE,
  'Semi-Detached': LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_PROPERTY_TYPE_SEMI_DETACHED,
  Other: LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_PROPERTY_TYPE_OTHER,
};

function translatePropertyLabel(label: string, t: TFunction): string {
  const key = PROPERTY_TYPE_LABEL_KEYS[label];
  return key ? t(key, { defaultValue: label }) : label;
}

type CardLabelKey = 'totalListings' | 'volumeDisclaimer' | 'medianPriceTrend' | 'daysOnMarket' | 'listRatio';

/** Card labels per market, as [translation key, English default]. */
const CARD_LABELS: Record<InsightsListingType, Record<CardLabelKey, [string, string]>> = {
  sale: {
    totalListings: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_TOTAL_LISTINGS, 'Total Listings*'],
    volumeDisclaimer: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_VOLUME_DISCLAIMER, '*Market volume data is taken from the total number of houses, townhouses, and condos on eXp Realty. This does not include other listings such as land, farm, or commercial.'],
    medianPriceTrend: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_MEDIAN_PRICE_TREND, 'Median Price Trend'],
    daysOnMarket: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_AVG_DAYS_ON_MARKET, 'Avg. Days on Market'],
    listRatio: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_SELL_TO_LIST_RATIO, 'Sell-to-List Ratio'],
  },
  rent: {
    totalListings: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_RENT_TOTAL_LISTINGS, 'Total Rentals*'],
    volumeDisclaimer: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_RENT_VOLUME_DISCLAIMER, '*Rental volume is the number of house, townhouse, and condo rentals on eXp Realty, grouped by average monthly rent. This does not include other rentals such as land or commercial.'],
    medianPriceTrend: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_RENT_MEDIAN_PRICE_TREND, 'Median Asking Rent Trend'],
    daysOnMarket: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_RENT_AVG_DAYS_ON_MARKET, 'Avg. Days to Lease'],
    listRatio: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_RENT_SELL_TO_LIST_RATIO, 'Lease-to-List Ratio'],
  },
  sold: {
    totalListings: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_SOLD_TOTAL_LISTINGS, 'Homes Sold*'],
    volumeDisclaimer: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_SOLD_VOLUME_DISCLAIMER, '*Sales volume is the number of houses, townhouses, and condos sold in the last 90 days. This does not include other sales such as land, farm, or commercial.'],
    medianPriceTrend: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_SOLD_MEDIAN_PRICE_TREND, 'Median Sold Price Trend'],
    daysOnMarket: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_AVG_DAYS_ON_MARKET, 'Avg. Days on Market'],
    listRatio: [LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_SELL_TO_LIST_RATIO, 'Sell-to-List Ratio'],
  },
};

function cardLabel(listingType: InsightsListingType, key: CardLabelKey, t: TFunction): string {
  const [translationKey, defaultValue] = CARD_LABELS[listingType][key];
  return t(translationKey, { defaultValue });
}

// ── Component ───────────────────────────────────────────────

export default function MarketDynamics({
  locationName,
  propertyTypes,
  volume,
  isLoading,
  priceTrends,
  marketHealth,
  locationUrl,
  description,
  listingType = 'sale',
}: MarketDynamicsProps) {
  const { t } = useTranslation(Namespaces.HOME);
  const localizedPropertyTypes = useMemo(
    () => propertyTypes.map(seg => ({ ...seg, label: translatePropertyLabel(seg.label, t) })),
    [propertyTypes, t]
  );
  return (
    <div className={styles['market-dynamics']}>
      {/* Section heading — fade up */}
      <ScrollReveal className={styles['heading-dropdown-layer']}>
        <div className={parentStyles['section-heading']}>
          <span className={parentStyles['heading-text']}>
            {locationName
              ? t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_HEADING_IN, { defaultValue: 'Market Dynamics in ' })
              : t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_HEADING, { defaultValue: 'Market Dynamics ' })}
          </span>
          {locationName && (
            locationUrl ? (
              <Link href={locationUrl} className={parentStyles['heading-location']}>
                {locationName}
              </Link>
            ) : (
              <span className={parentStyles['heading-text']}>{locationName}</span>
            )
          )}
        </div>
        {description && <p className={styles['section-description']}>{description}</p>}
      </ScrollReveal>

      {isLoading ? (
        <div className={styles['cards-row']}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={styles.card}>
              <div className={skeleton.bone} style={{ width: '60%', height: 14, marginBottom: 16 }} />
              <div className={skeleton.bone} style={{ width: '100%', height: 160, borderRadius: 16 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                <div className={skeleton.bone} style={{ width: '80%', height: 12 }} />
                <div className={skeleton.bone} style={{ width: '55%', height: 12 }} />
                <div className={skeleton.bone} style={{ width: '70%', height: 12 }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <StaggerReveal staggerDelay={0.08} className={styles['cards-row']}>
          {/* Property Type Distribution — only when real data is available */}
          {localizedPropertyTypes.length > 0 && (
            <div className={buildClassName(styles.card, styles['distribution-card'])}>
              <div className={styles['card-label']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_PROPERTY_TYPE_DISTRIBUTION, { defaultValue: 'Property Type Distribution' })}</div>

              <div className={styles['pie-section']}>
                <PropertyPieChart segments={localizedPropertyTypes} />
              </div>

              {/* Legend */}
              <div className={styles.legend}>
                {localizedPropertyTypes.map(seg => (
                  <div key={seg.label} className={styles['legend-item']}>
                    <span className={styles['legend-swatch']} style={{ background: seg.color }} />
                    <span className={styles['legend-label']}>{seg.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Market Volume — only when real data is available */}
          {volume && (
            <div className={buildClassName(styles.card, styles['volume-card'])}>
              <div className={styles['volume-header']}>
                <div className={styles['volume-info']}>
                  <div className={styles['card-label']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_MARKET_VOLUME, { defaultValue: 'Market Volume' })}</div>
                  <div className={styles['volume-value-row']}>
                    <AnimatedCounter
                      end={volume.totalListings}
                      duration={1600}
                      className={styles['volume-value']}
                    />
                    <span className={styles['volume-suffix']}>{cardLabel(listingType, 'totalListings', t)}</span>
                  </div>
                </div>
                <div className={styles['volume-highlight']}>
                  <span className={styles['highlight-label']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_MOST_ACTIVE, { defaultValue: 'Most Active' })}</span>
                  <span className={styles['highlight-badge']}>{volume.mostActiveRange}</span>
                </div>
              </div>

              <div className={styles['bar-chart']}>
                {volume.priceRanges.map(range => (
                  <div key={range.label} className={styles['bar-item']}>
                    <div className={styles['bar-header']}>
                      <span className={range.isActive ? styles['bar-label-active'] : styles['bar-label']}>
                        {range.label}
                      </span>
                      <span className={range.isActive ? styles['bar-percentage-active'] : styles['bar-percentage']}>
                        {range.percentage}%{range.isActive && t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_MOST_ACTIVE_INLINE, { defaultValue: ' (Most Active)' })}
                      </span>
                    </div>
                    <div className={styles['bar-track']}>
                      <ProgressFill
                        percentage={range.percentage}
                        className={range.isActive ? styles['bar-fill-active'] : styles['bar-fill']}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <p className={styles['volume-disclaimer']}>
                {cardLabel(listingType, 'volumeDisclaimer', t)}
              </p>
            </div>
          )}

          {/* Price Trend — sparkline chart */}
          {priceTrends && priceTrends.length > 2 && (
            <div className={buildClassName(styles.card, styles['trend-card'])}>
              <div className={styles['card-label']}>{cardLabel(listingType, 'medianPriceTrend', t)}</div>
              <PriceTrendChart data={priceTrends} />
            </div>
          )}

          {/* Market Health — key metrics */}
          {marketHealth && marketHealth.daysOnMarket > 0 && (
            <div className={buildClassName(styles.card, styles['health-card'])}>
              <div className={styles['card-label']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_MARKET_HEALTH, { defaultValue: 'Market Health' })}</div>
              <MarketHealthMetrics data={marketHealth} listingType={listingType} />
            </div>
          )}
        </StaggerReveal>
      )}
    </div>
  );
}

// ── Pie chart sub-component ─────────────────────────────────

function PropertyPieChart({ segments }: { segments: PropertyTypeSegment[] }) {
  const { t } = useTranslation(Namespaces.HOME);
  const total = useMemo(() => segments.reduce((s, seg) => s + seg.count, 0), [segments]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const arcs = useMemo(() => {
    if (total === 0) return [];

    const cx = 120;
    const cy = 120;
    const r = 100;
    const results: { d: string; color: string; label: string; percentage: number }[] = [];
    let cumulative = 0;

    segments.forEach(seg => {
      const pct = seg.count / total;
      const startAngle = cumulative * 2 * Math.PI - Math.PI / 2;
      cumulative += pct;
      const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;

      const largeArc = pct > 0.5 ? 1 : 0;

      const x1 = cx + r * Math.cos(startAngle);
      const y1 = cy + r * Math.sin(startAngle);
      const x2 = cx + r * Math.cos(endAngle);
      const y2 = cy + r * Math.sin(endAngle);

      const d = [
        `M ${cx} ${cy}`,
        `L ${x1.toFixed(2)} ${y1.toFixed(2)}`,
        `A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`,
        'Z',
      ].join(' ');

      results.push({ d, color: seg.color, label: seg.label, percentage: Math.round(pct * 100) });
    });

    return results;
  }, [segments, total]);

  if (total === 0) {
    return (
      <svg viewBox="0 0 240 240" className={styles['pie-svg']}>
        <circle cx={120} cy={120} r={100} fill="#f1f5f9" />
        <text x={120} y={125} textAnchor="middle" fill="#94a3b8" fontSize={14}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_NO_DATA, { defaultValue: 'No data' })}</text>
      </svg>
    );
  }

  const hovered = hoveredIndex !== null ? arcs[hoveredIndex] : null;

  return (
    <svg viewBox="0 0 240 240" className={styles['pie-svg']}>
      {arcs.map((arc, i) => (
        <path
          key={arc.label}
          d={arc.d}
          fill={arc.color}
          stroke="white"
          strokeWidth={2}
          className={styles['pie-slice']}
          opacity={hoveredIndex !== null && hoveredIndex !== i ? 0.45 : 1}
          onMouseEnter={() => setHoveredIndex(i)}
          onMouseLeave={() => setHoveredIndex(null)}
        />
      ))}
      {hovered && (
        <>
          <circle cx={120} cy={120} r={42} fill="white" fillOpacity={0.92} className={styles['pie-center-bg']} />
          <text x={120} y={114} textAnchor="middle" className={styles['pie-center-pct']}>
            {hovered.percentage}%
          </text>
          <text x={120} y={134} textAnchor="middle" className={styles['pie-center-label']}>
            {hovered.label}
          </text>
        </>
      )}
    </svg>
  );
}

// ── Price Trend sparkline sub-component ─────────────────────

function PriceTrendChart({ data }: { data: PriceTrendPoint[] }) {
  const { points, areaPoints, lastPrice, change, isUp } = useMemo(() => {
    const prices = data.map(d => d.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const range = max - min || 1;

    const W = 280;
    const H = 100;
    const pad = 4;

    const pts = data.map((d, i) => {
      const x = pad + (i / (data.length - 1)) * (W - 2 * pad);
      const y = H - pad - ((d.price - min) / range) * (H - 2 * pad);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });

    const fp = data[0].price;
    const lp = data[data.length - 1].price;
    const ch = ((lp - fp) / fp) * 100;

    return {
      points: pts.join(' '),
      areaPoints: `${pad},${H - pad} ${pts.join(' ')} ${W - pad},${H - pad}`,
      lastPrice: lp,
      change: ch,
      isUp: ch >= 0,
    };
  }, [data]);

  return (
    <div className={styles['trend-content']}>
      <div className={styles['trend-header']}>
        <span className={styles['trend-value']}>${Math.round(lastPrice).toLocaleString()}</span>
        <span className={buildClassName(
          styles['trend-change'],
          isUp ? styles['change-up'] : styles['change-down']
        )}>
          {isUp ? '↗' : '↘'} {Math.abs(change).toFixed(1)}%
        </span>
      </div>
      <svg viewBox="0 0 280 100" className={styles['trend-svg']} preserveAspectRatio="none">
        <defs>
          <linearGradient id="trendFill" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={isUp ? '#10b981' : '#ef4444'} stopOpacity="0.18" />
            <stop offset="100%" stopColor={isUp ? '#10b981' : '#ef4444'} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={areaPoints} fill="url(#trendFill)" />
        <polyline
          points={points}
          fill="none"
          stroke={isUp ? '#10b981' : '#ef4444'}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div className={styles['trend-dates']}>
        <span>{formatMonth(data[0].date)}</span>
        <span>{formatMonth(data[data.length - 1].date)}</span>
      </div>
    </div>
  );
}

// ── Market Health sub-component ─────────────────────────────

function MarketHealthMetrics({ data, listingType }: { data: MarketHealthData; listingType: InsightsListingType }) {
  const { t } = useTranslation(Namespaces.HOME);
  return (
    <div className={styles['health-content']}>
      <div className={styles['health-metric']}>
        <div className={styles['health-metric-value']}>
          <AnimatedCounter end={data.daysOnMarket} duration={1000} />
          <span className={styles['health-metric-unit']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_DAYS_UNIT, { defaultValue: ' days' })}</span>
        </div>
        <div className={styles['health-metric-label']}>{cardLabel(listingType, 'daysOnMarket', t)}</div>
      </div>

      <div className={styles['health-metric']}>
        <div className={styles['health-metric-value']}>
          {formatRatio(data.sellListRatio)}
        </div>
        <div className={styles['health-metric-label']}>{cardLabel(listingType, 'listRatio', t)}</div>
      </div>

      {data.avgPricePerSqft > 0 && (
        <div className={styles['health-metric']}>
          <div className={styles['health-metric-value']}>
            <AnimatedCounter end={data.avgPricePerSqft} duration={1000} prefix="$" />
            <span className={styles['health-metric-unit']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_SQFT_UNIT, { defaultValue: '/sqft' })}</span>
          </div>
          <div className={styles['health-metric-label']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_AVG_PRICE_PER_SQFT, { defaultValue: 'Avg. Price per Sq.Ft' })}</div>
        </div>
      )}

      {data.medianRentPrice > 0 && (
        <div className={styles['health-metric']}>
          <div className={styles['health-metric-value']}>
            <AnimatedCounter end={data.medianRentPrice} duration={1000} prefix="$" />
            <span className={styles['health-metric-unit']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_MONTH_UNIT, { defaultValue: '/mo' })}</span>
          </div>
          <div className={styles['health-metric-label']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_MARKET_DYNAMICS_MEDIAN_RENT, { defaultValue: 'Median Rent' })}</div>
        </div>
      )}
    </div>
  );
}
